import { initializeApp } from 'firebase/app'
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  getToken,
} from 'firebase/app-check'

// Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyBY4BsIdW0ljOgb-vz-Wq2jnNfwxyRW7xo',
  authDomain: 'maestro-connect-2e3f0.firebaseapp.com',
  projectId: 'maestro-connect-2e3f0',
  storageBucket: 'maestro-connect-2e3f0.firebasestorage.app',
  messagingSenderId: '126157978509',
  appId: '1:126157978509:web:1a4145c92da89aa225dbc4',
}

// FIXED: Safe environment variable access for Vite with fallback to window.location.origin
const getBaseUrl = (): string => {
  // PRODUCTION FIX: Always use production domain for email verification
  // This ensures verification emails contain the correct domain regardless of where the code runs
  return 'https://maestroconnect.io'
}

export const actionCodeSettings = {
  // CRITICAL: Use production domain for email verification links
  url: 'https://maestroconnect.io/email-verified',
  // This must be true for email verification
  handleCodeInApp: true,
  // Adding iOS and Android settings for completeness
  iOS: {
    bundleId: 'com.maestroconnect.app',
  },
  android: {
    packageName: 'com.maestroconnect.app',
    installApp: true,
    minimumVersion: '12',
  },
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// AppCheck: always initialize unconditionally.
// The previous `import.meta.env?.PROD` gate evaluated to false in the
// Magic Patterns hosting pipeline, which caused App Check to never
// initialize in production (resulting in 0% verified requests in
// Firebase Console). Removing the gate is safe because:
//   1. Firestore and Storage enforcement are in Monitoring mode, so
//      tokens issued in any environment are non-blocking.
//   2. reCAPTCHA v3 has no meaningful rate limits for dev sessions.
//   3. This is the simplest, lowest-risk fix.
try {
  const appCheckInstance = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(
      '6LcYVkIsAAAAAB5EubLJ4HP4yZFVfPSy5Tuf9lc9',
    ),
    isTokenAutoRefreshEnabled: true,
  })
  console.log('[Firebase] AppCheck initialized')

  // App Check token pre-warm — DO NOT REMOVE.
  //
  // Why this is required:
  //   `initializeAppCheck` registers the provider synchronously but does
  //   NOT eagerly fetch the first token. The first token is acquired
  //   lazily, when a Firebase service requests one.
  //
  //   Firestore's WebChannel (long-polling) connection opens once per
  //   session and reuses the same connection for the rest of the session.
  //   The X-Firebase-AppCheck header is attached at channel-open time.
  //   If the channel happens to open before App Check's first token is
  //   ready, the channel proceeds WITHOUT a token and stays unauthenticated
  //   for the rest of the session — even after subsequent tokens are
  //   acquired. This is what caused Firebase Console to report 100%
  //   unverified Firestore requests before this pre-warm was added.
  //
  //   By calling getToken() immediately at module load (well before any
  //   React component mounts and triggers a Firestore query), we ensure
  //   the App Check cache is warm when the WebChannel opens. Verified
  //   via Network tab: X-Firebase-AppCheck header is now present on every
  //   firestore.googleapis.com/Listen/channel request.
  //
  //   Cost: one extra reCAPTCHA exchange POST per page load.
  //   Benefit: 0% → near-100% verified Firestore + Storage requests.
  //
  //   Failure path is logged (not silent) so ops/Sentry can detect App
  //   Check outages. Success path is intentionally silent — this is
  //   normal startup behavior, not a debug signal.
  getToken(appCheckInstance, /* forceRefresh */ true).catch((error) => {
    console.error(
      '[Firebase] AppCheck pre-warm failed:',
      error?.code || error?.name,
      error?.message,
    )
  })
} catch (error) {
  console.error('[Firebase] AppCheck initialization failed:', error)
  // Continue without AppCheck if it fails — Monitoring mode means
  // requests are not blocked by missing tokens.
}

// Get Firebase services
export const auth = getAuth(app)

// CRITICAL FIX: Set persistence to LOCAL for mobile reliability
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('[Auth] Persistence set to browserLocalPersistence')
  })
  .catch((error) => {
    console.error('[Auth] Failed to set persistence:', error)
  })

export const db = getFirestore(app)
export const storage = getStorage(app)
export const functions = getFunctions(app)

export default app
